import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis, snapCenterToCursor } from '@dnd-kit/modifiers';
import { GripVertical, Star, Tv, Film, Check, Info, MoreVertical, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

export function SortableSeriesList({ series, onReorder, onViewDetails, onRemove, onMarkWatched, onRerank, disabled }) {
  const [activeId, setActiveId] = useState(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const activeItem = activeId ? series.find(s => s.tmdb_id === activeId) : null;

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    
    if (active.id !== over?.id) {
      const oldIndex = series.findIndex(s => s.tmdb_id === active.id);
      const newIndex = series.findIndex(s => s.tmdb_id === over.id);
      const newOrder = arrayMove(series, oldIndex, newIndex);
      onReorder(newOrder.map(s => s.tmdb_id));
    }
  };

  return (
    <DndContext 
      sensors={sensors} 
      collisionDetection={closestCenter} 
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={series.map(s => s.tmdb_id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {series.map((item, index) => (
            <SortableSeriesCard
              key={item.tmdb_id}
              series={item}
              index={index}
              onViewDetails={() => onViewDetails(item)}
              onRemove={() => onRemove(item.tmdb_id)}
              onMarkWatched={() => onMarkWatched(item)}
              onRerank={onRerank ? () => onRerank(item) : null}
              disabled={disabled}
            />
          ))}
        </div>
      </SortableContext>
      
      {createPortal(
        <DragOverlay modifiers={[snapCenterToCursor]}>
          {activeItem && (
            <DragOverlayCard series={activeItem} />
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}

// Drag overlay card - displays during drag
function DragOverlayCard({ series }) {
  const isMovie = series.media_type === 'movie';
  const Icon = isMovie ? Film : Tv;
  
  return (
    <div className="flex items-center gap-2 sm:gap-3 bg-white p-2 sm:p-3 rounded-xl border-2 border-[#F87171] shadow-xl">
      <div className="p-1 rounded hover:bg-slate-100 flex-shrink-0">
        <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
      </div>
      <span className="font-outfit font-bold text-base sm:text-lg text-[#F87171] w-6 sm:w-8 text-center">?</span>
      <div className="w-8 h-12 sm:w-10 sm:h-14 rounded overflow-hidden flex-shrink-0 bg-slate-100">
        {series.poster_path ? (
          <img 
            src={`https://image.tmdb.org/t/p/w92${series.poster_path}`}
            alt={series.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-[#3D4F6F] truncate text-sm sm:text-base">{series.title}</h3>
        <div className="flex items-center gap-2 text-[#64748B] text-xs sm:text-sm">
          <Icon className="w-3 h-3" />
          <span className="capitalize">{series.media_type || 'tv'}</span>
        </div>
      </div>
    </div>
  );
}

function SortableSeriesCard({ series, index, onViewDetails, onRemove, onMarkWatched, onRerank, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: series.tmdb_id,
    disabled 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const isMovie = series.media_type === 'movie';

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`series-card-${index}`}
      className={`flex items-center gap-2 sm:gap-3 bg-white p-2 sm:p-3 rounded-xl border shadow-sm transition-all ${
        isDragging ? 'border-[#F87171] shadow-lg' : 'border-slate-100 hover:border-[#F87171]/30'
      }`}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className={`cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-100 flex-shrink-0 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
      </div>

      {/* Rank Number */}
      <span className="font-outfit font-bold text-lg sm:text-xl text-[#3D4F6F] w-6 sm:w-8 text-center flex-shrink-0">
        {index + 1}
      </span>

      {/* Poster - Clickable for details */}
      <div 
        className="flex-shrink-0 cursor-pointer" 
        onClick={onViewDetails}
        data-testid={`poster-${index}`}
      >
        {series.poster_path ? (
          <img
            src={series.poster_path}
            alt={series.title}
            className="w-10 h-14 sm:w-12 sm:h-16 object-cover rounded-lg hover:scale-105 transition-transform shadow-sm"
          />
        ) : (
          <div className="w-10 h-14 sm:w-12 sm:h-16 bg-slate-200 rounded-lg flex items-center justify-center">
            {isMovie ? <Film className="w-4 h-4 text-slate-400" /> : <Tv className="w-4 h-4 text-slate-400" />}
          </div>
        )}
      </div>

      {/* Title Only - Compact on mobile */}
      <div className="flex-1 min-w-0 cursor-pointer" onClick={onViewDetails}>
        <h3 className="font-medium text-sm sm:text-base text-[#3D4F6F] hover:text-[#F87171] transition-colors truncate">
          {series.title}
        </h3>
        {/* Show year only on larger screens */}
        <p className="hidden sm:block text-xs text-[#64748B]">
          {series.first_air_date && new Date(series.first_air_date).getFullYear()}
          {series.vote_average > 0 && (
            <span className="ml-2 inline-flex items-center gap-0.5">
              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              {series.vote_average.toFixed(1)}
            </span>
          )}
        </p>
      </div>

      {/* Actions - Compact */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Info Button - Mobile only */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 sm:hidden text-[#64748B] hover:text-[#F87171]"
          onClick={onViewDetails}
          data-testid={`info-btn-${index}`}
        >
          <Info className="w-4 h-4" />
        </Button>
        
        {/* Watched Button - Icon on mobile, full on desktop */}
        <Button
          data-testid={`mark-watched-btn-${index}`}
          onClick={onMarkWatched}
          size="icon"
          className="h-8 w-8 sm:h-9 sm:w-auto sm:px-3 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
        >
          <Check className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Watched</span>
        </Button>
        
        {/* Edit Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-8 w-8 text-[#64748B] hover:text-[#3D4F6F]"
              data-testid={`edit-btn-${index}`}
            >
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onRerank && (
              <DropdownMenuItem onClick={onRerank} className="cursor-pointer">
                <RefreshCw className="w-4 h-4 mr-2 text-purple-600" />
                Re-rank
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onRemove} className="cursor-pointer text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// Compact version for ranking dialogs
export function SortableRankingList({ items, onReorder, highlightId }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(s => s.tmdb_id === active.id);
      const newIndex = items.findIndex(s => s.tmdb_id === over.id);
      const newOrder = arrayMove(items, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(s => s.tmdb_id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {items.map((item, index) => (
            <SortableRankingItem
              key={item.tmdb_id}
              item={item}
              index={index}
              isHighlighted={item.tmdb_id === highlightId}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRankingItem({ item, index, isHighlighted }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: item.tmdb_id 
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
        isHighlighted 
          ? 'border-[#F87171] bg-[#F87171]/5' 
          : isDragging 
            ? 'border-[#F87171] bg-white shadow-lg' 
            : 'border-slate-200 bg-white'
      }`}
    >
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-100"
      >
        <GripVertical className="w-4 h-4 text-slate-400" />
      </div>
      <span className={`font-outfit font-bold text-lg w-6 text-center ${
        isHighlighted ? 'text-[#F87171]' : 'text-[#3D4F6F]'
      }`}>
        {index + 1}
      </span>
      {item.poster_path && (
        <img src={item.poster_path} alt={item.title} className="w-8 h-12 object-cover rounded" />
      )}
      <span className={`flex-1 font-medium truncate ${isHighlighted ? 'text-[#F87171]' : 'text-[#3D4F6F]'}`}>
        {item.title}
        {isHighlighted && (
          <span className="ml-2 text-xs bg-[#F87171] text-white px-2 py-0.5 rounded-full">NEW</span>
        )}
      </span>
    </div>
  );
}
